import * as Sentry from '@sentry/react';
import { isAbortError } from '../utils/isAbortError';
import { shouldReportToSentry } from './errorFilters';

const isProduction = process.env.NODE_ENV === 'production';

const isAbortMessage = (msg = '') => {
  const lower = String(msg).toLowerCase();
  return (
    lower.includes('the operation was aborted') ||
    lower.includes('aborterror') ||
    lower.includes('signal is aborted') ||
    lower.includes('request was aborted') ||
    lower.includes('err_canceled') ||
    lower.includes('cancelederror')
  );
};

/** Abort-shaped errors and string rejections (e.g. Promise.reject("AbortError: …")). */
function isAbortLike(value) {
  if (isAbortError(value)) return true;
  if (value == null) return false;
  if (typeof value === 'string') return isAbortMessage(value);
  if (typeof value === 'object' && typeof value.message === 'string') {
    return isAbortMessage(value.message);
  }
  return isAbortMessage(String(value));
}

function shouldDropAsAbortNoise(error, eventMessage) {
  return isAbortLike(error) || isAbortMessage(eventMessage);
}

/** Never report to Sentry when the app is running on a local dev origin. */
function isLocalhostOrigin() {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname;
  return (
    h === 'localhost' ||
    h === '127.0.0.1' ||
    h === '[::1]' ||
    h === '::1' ||
    h.endsWith('.localhost')
  );
}

function getCircularSafeReplacer() {
  const seen = new WeakSet();
  return (_key, value) => {
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) return '[Circular]';
      seen.add(value);
    }
    return value;
  };
}

/** Serialize non-Error rejection reasons for Sentry (avoids useless "Unknown rejection" events). */
function serializeRejectionReason(reason) {
  if (reason === null || reason === undefined) {
    return String(reason);
  }
  if (typeof reason !== 'object') {
    return String(reason);
  }
  const ctor = reason.constructor?.name;
  const prefix = ctor && ctor !== 'Object' ? `[${ctor}] ` : '';
  try {
    const s = JSON.stringify(reason, getCircularSafeReplacer());
    const body = s.length > 2000 ? `${s.slice(0, 2000)}…` : s;
    return `${prefix}${body}`;
  } catch (_) {
    try {
      return prefix + Object.prototype.toString.call(reason);
    } catch (__) {
      return `${prefix}[object]`;
    }
  }
}

/**
 * Returns true for cross-realm Error instances (e.g. thrown inside iframes or
 * third-party scripts) that fail the `instanceof Error` check but still have
 * the shape of an Error object.
 */
function isErrorLike(value) {
  return (
    value !== null &&
    typeof value === 'object' &&
    typeof value.message === 'string' &&
    typeof value.stack === 'string'
  );
}

/**
 * @returns {{ error: Error, captureExtra?: Record<string, string> }}
 */
function rejectionReasonToError(reason) {
  if (reason instanceof Error) {
    return { error: reason };
  }

  // Cross-realm Error (e.g. from an iframe or third-party script).
  // instanceof fails across realms, but the shape is still Error-like.
  if (isErrorLike(reason)) {
    const error = new Error(reason.message || '(empty message)');
    error.name = reason.name || reason.constructor?.name || 'Error';
    error.stack = reason.stack;
    return {
      error,
      captureExtra: {
        rejectionReasonType: 'cross-realm-error',
        rejectionReasonValue: reason.message || '',
      },
    };
  }

  if (typeof reason === 'string') {
    const error = new Error(reason);
    error.name = 'NonErrorRejection';
    error.originalValue = reason;
    return {
      error,
      captureExtra: {
        rejectionReasonType: 'string',
        rejectionReasonValue: reason,
      },
    };
  }

  const type =
    reason === null
      ? 'null'
      : reason === undefined
        ? 'undefined'
        : reason.constructor?.name || typeof reason;
  const serialized = serializeRejectionReason(reason);
  const error = new Error(`Unhandled promise rejection (${type}): ${serialized}`);
  error.name = 'UnhandledPromiseRejection';
  return {
    error,
    captureExtra: {
      rejectionReasonType: type,
      rejectionReasonValue: serialized,
    },
  };
}

function setupGlobalErrorHandlers() {
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event?.reason;

    if (isAbortLike(reason)) {
      if (isProduction) {
        try {
          event.preventDefault?.();
        } catch (_) {}
      }
      if (Math.random() < 0.1) {
        Sentry.addBreadcrumb({
          category: 'network',
          message: 'Request aborted (expected)',
          level: 'info',
          data: {
            reason: serializeRejectionReason(reason),
          },
        });
      }
      return;
    }

    if (!shouldReportToSentry(reason)) {
      if (isProduction) {
        try {
          event.preventDefault?.();
        } catch (_) {}
      }
      return;
    }

    const { error, captureExtra } = rejectionReasonToError(reason);

    if (!shouldReportToSentry(error)) {
      if (isProduction) {
        try {
          event.preventDefault?.();
        } catch (_) {}
      }
      return;
    }

    if (isProduction) {
      try {
        event.preventDefault?.();
      } catch (_) {}
    }

    Sentry.captureException(error, {
      ...(captureExtra && { extra: captureExtra }),
      tags: {
        source: 'global-handler',
      },
      fingerprint: captureExtra
        ? ['unhandled-rejection', captureExtra.rejectionReasonType || 'unknown']
        : ['unhandled-rejection', error.name || 'Error'],
      contexts: {
        unhandledRejection: {
          handled: false,
        },
      },
    });
  });

  window.addEventListener('error', (event) => {
    const candidate = event?.error ?? event?.message;
    if (isAbortLike(candidate)) {
      return;
    }

    const eventMessage = event?.message || candidate?.message || String(candidate ?? '');
    if (!shouldReportToSentry(candidate, eventMessage)) {
      if (isProduction) {
        try {
          event.preventDefault?.();
        } catch (_) {}
      }
      return;
    }

    if (
      event.message &&
      !event.message.includes('ResizeObserver loop completed with undelivered notifications') &&
      !event.message.includes('ResizeObserver loop limit exceeded')
    ) {
      if (isProduction) {
        try {
          event.preventDefault?.();
        } catch (_) {}
      }

      Sentry.captureException(event.error || new Error(event.message), {
        tags: {
          source: 'global-handler',
        },
        fingerprint: [
          'window-error',
          event.filename || 'unknown',
          String(event.lineno ?? ''),
        ],
        contexts: {
          windowError: {
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
          },
        },
      });
    }
  });
}

/**
 * Initialize Sentry. Call once before React renders.
 */
export function initSentry() {
  const isNonProduction = process.env.NODE_ENV !== 'production';
  // NODE_ENV is always 'development'|'test'|'production' in CRA — never 'development-local'.
  // Use REACT_APP_ENV (set in .env.development-local) to detect the local override env.
  const isLocal = process.env.REACT_APP_ENV === 'development-local';

  if (isLocal && !process.env.REACT_APP_ENABLE_SENTRY) {
    return;
  }

  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn) {
    return;
  }

  Sentry.init({
    dsn,
    environment: process.env.NODE_ENV || 'development',
    tracesSampleRate: isNonProduction ? 1.0 : 0.1,
    replaysSessionSampleRate: isNonProduction ? 1.0 : 0.1,
    replaysOnErrorSampleRate: 1.0,
    // Avoid sending IP / default PII unless you explicitly need it (GDPR / policy).
    sendDefaultPii: false,

    // Custom listeners below replace GlobalHandlers (avoids duplicate onerror / unhandledrejection).
    // Keep TryCatch so wrapped timers / XHR still report without duplicating window listeners.
    integrations: (defaults) =>
      defaults.filter((integration) => integration.name !== 'GlobalHandlers'),

    beforeSend(event, hint) {
      if (isLocalhostOrigin()) {
        return null;
      }

      const error = hint?.originalException;

      const eventMessage =
        event?.message || event?.exception?.values?.[0]?.value || '';

      if (shouldDropAsAbortNoise(error, eventMessage)) {
        return null;
      }

      if (!shouldReportToSentry(error, eventMessage)) {
        return null;
      }

      if (
        error?.message &&
        (error.message.includes('ResizeObserver loop completed with undelivered notifications') ||
          error.message.includes('ResizeObserver loop limit exceeded'))
      ) {
        return null;
      }

      if (typeof eventMessage === 'string') {
        const lower = eventMessage.toLowerCase();
        if (lower.includes('matrix.org') || lower.includes('matrix')) {
          event.tags = {
            ...event.tags,
            matrix_sdk: 'true',
          };
        }
      }

      return event;
    },

    // Backup net for raw browser/SDK errors — primary filters live in errorFilters.js (shouldReportToSentry).
    ignoreErrors: [
      'top.GLOBALS',
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'atomicFindClose',
      'fb_xd_fragment',
      'bmi_SafeAddOnload',
      'EBCallBackMessageReceived',
      'conduitPage',
      // Thrown in Instagram/Facebook/Twitter in-app browsers (WKWebView without our native
      // message handlers injected). The native bridge is only available inside our iOS app —
      // these visitors came from a social deep-link and window.webkit.messageHandlers is undefined.
      /window\.webkit\.messageHandlers/,
      /Unknown room !/,
      // Expired Matrix access token while the client refreshes; not all 401s.
      /M_UNKNOWN_TOKEN/i,
      /Access token has expired/i,
      // Membership-lost /messages 403 (peer left or deleted account); not all M_FORBIDDEN.
      /M_FORBIDDEN:.*not in room/i,
      /room previews are disabled/i,
      /Failed to execute 'removeChild' on 'Node'/,
      /timeout of \d+ms exceeded/,
      /Object captured as exception/,
    ],

    denyUrls: [
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
      /^safari-web-extension:\/\//i,
      /maps\.googleapis\.com/i,
      /maps\.gstatic\.com/i,
    ],

    release: process.env.REACT_APP_VERSION || undefined,
  });

  setupGlobalErrorHandlers();
}
